/* global window, t */
// Backend-aware read model for AI Regnskapsfører. It derives customer-facing tasks from
// existing Plumme backend models, but never executes sensitive mutations directly.

const apArray = (value) => Array.isArray(value) ? value : [];
const apAgentText = (value) => typeof value === 'string' ? value : '';
const apNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};
const AP_BANK_RECONCILE_KINDS = ['operating', 'tax_withholding'];
const apBankAccountIdentity = (account) => {
  const accountNumber = String(account?.accountNumber || '').replace(/\D/g, '');
  return accountNumber ? `number:${accountNumber}` : `id:${String(account?.id || '')}`;
};
const apBankReconcileAccounts = (state) => {
  const accounts = apArray(state?.settings?.bankAccounts)
    .filter((account) => account?.active !== false)
    .filter((account) =>
      AP_BANK_RECONCILE_KINDS.includes(String(account?.kind || 'operating')) || account?.reconcile === true);
  const unique = new Map();
  accounts.forEach((account) => {
    const identity = apBankAccountIdentity(account);
    const existing = unique.get(identity);
    const aliasIds = [...new Set([
      ...(existing?._apBankAccountIds || []),
      String(account?.id || ''),
    ].filter(Boolean))];
    unique.set(identity, existing
      ? { ...account, ...existing, _apBankAccountIds: aliasIds }
      : { ...account, _apBankAccountIds: aliasIds });
  });
  return [...unique.values()];
};
const apHasBankReconciliationScope = (state) => {
  const activeAccounts = apArray(state?.settings?.bankAccounts).filter((account) => account?.active !== false);
  if (!activeAccounts.length) return true;
  return apBankReconcileAccounts(state).length > 0;
};

// Datofilteret bor i `autopilot-periods.jsx`, slik at agentene og fargene paa
// maanedsknappene ikke kan bli uenige om hva «januar» betyr.
const apInPeriod = (value, year, month) => Boolean(window.apPeriodIn?.(value, year, month));
const apBankTaskStatementSettled = (statement) => {
  const status = String(statement?.status || '').toLowerCase();
  return ['archived', 'reconciled', 'booked'].includes(status) || Boolean(statement?._backend?.bookedAt || statement?.bookedAt || statement?.reconciledAt);
};
const apBankTaskStatementCovers = (statement, year, month) => {
  if (month == null) return true;
  const from = new Date(statement?.periodFrom);
  const to = new Date(statement?.periodTo || statement?.periodFrom);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;
  return from <= new Date(year, Number(month) + 1, 0) && to >= new Date(year, Number(month), 1);
};
const apBankTaskLineStatus = (line) => {
  const raw = line?._backend || {};
  if (raw.reconciliationStatus === 'import-removed' || raw.importCorrectionAction === 'removed') return 'korrigert';
  if (raw.matchedEntryId) return raw.bookingType === 'existing-entry' ? 'matchet' : 'bokfort';
  if (raw.classification === 'private') return 'privat';
  if (raw.reconciliationStatus === 'approved') return 'godkjent';
  return String(line?.status || '').toLowerCase();
};
const AP_BANK_TASK_REVIEW_STATUSES = ['ny', 'forslag', 'avvik'];
const AP_BANK_TASK_HANDLED_STATUSES = ['godkjent', 'matchet', 'bokfort', 'bokført', 'privat', 'drift', 'ignorer', 'korrigert'];

// `checked` er antallet enheter agenten faktisk vurderte. Uten den kunne en tom
// liste bli 'done', og «Alle kjop i februar har bilag» ble sagt om en maaned
// uten et eneste kjop. Fravaer av data er ikke en godkjenning.
const apTaskStatus = ({ blocked = false, waiting = false, reviewCount = 0, readyCount = 0, done = false, checked = null } = {}) => {
  if (blocked) return 'blocked';
  if (waiting) return 'waiting';
  if (done && checked !== 0) return 'done';
  if (reviewCount > 0) return 'needs_review';
  if (readyCount > 0) return 'ready';
  return 'idle';
};

// NB: backend-tasks har med vilje ingen `conf`. Vi har ingen modell som gir en
// reell sikkerhetsscore per oppgave, og en utledet konstant ville vært et
// oppdiktet tall ved siden av regnskapstall. UI viser derfor ingen prosent for
// backend-tasks.

// Linjelistene var kappet paa 8 uten at noe sa ifra: med 40 bilag i innboksen
// saa du 8 og trodde det var alt. Modalen paginerer naa, saa taket er bare en
// sperre mot at en enkelt oppgave drar hele state-objektet med seg.
const AP_LINE_CAP = 500;

// ------------------------------------------------- hva en godkjenning dekker
//
// En signatur sier «jeg har gaatt gjennom dette». Uten et spor av HVA som ble
// gjennomgaatt, dekker den alt som senere skjer i samme periode: laster du opp
// en bankutskrift etter at bilagsagenten er godkjent, staar raden gronn selv om
// den naa finner to linjer uten bilag.
//
// Derfor faar hver oppgave et kort fingeravtrykk av grunnlaget sitt. Det lagres
// sammen med godkjenningen, og sammenlignes ved hver visning. Avviker det, er
// signaturen fortsatt gyldig som spor — den slutter bare aa dekke.
//
// Prinsippet er ikke nytt i produktet: koen mister godkjenningen sin naar
// forslaget endres (`proposalSignature` i functions/autopilot/autopilotTaskQueue.ts).
// Dette er det samme, ett niva opp.
//
// FNV-1a. Vi trenger determinisme og korthet, ikke kryptografi — signaturen
// beskytter mot at data endrer seg i stillhet, ikke mot en motstander.
const apHash = (value) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36);
};
// Grunnlaget er statusen agenten kom fram til, hvor mye som stod igjen, og
// hvilke linjer det gjaldt. Titler og beloep holdes utenfor: en omskrevet
// setning er ikke en ny opplysning.
const apTaskBasis = (task) => {
  if (!task || task.queueGroup || task.queueTask) return '';
  const lines = Array.isArray(task.lines) ? task.lines : [];
  return apHash([
    task.id,
    task.status,
    task.readyCount || 0,
    task.reviewCount || 0,
    lines.length,
    lines.map((line) => line?.id).filter(Boolean).sort().join(','),
  ].join('|'));
};

const apTask = ({
  id,
  icon,
  title,
  desc,
  detail,
  status,
  route,
  actionLabel,
  readyCount = 0,
  reviewCount = 0,
  blockedReason = '',
  statusDetail = '',
  lines = [],
  inbox = [],
  kind = 'backend',
  due = '',
  execution = null,
  keepVisibleWhenDone = false,
  skippable = false,
  requiresCorrection = false,
  // 'month'   — hører til den valgte måneden og filtreres på dato.
  // 'running'  — løpende arbeid som gjelder nå uansett måned (forfalte krav).
  // 'year'     — register og årsoppgjør, uten en måned å høre hjemme i.
  scope = 'month', bankIntegrityIssue = false,
  // Kalendermåneden (0-indeksert) en årsoppgave har en frist i. En årsoppgave
  // vises normalt bare i inneværende måned, men en frist HAR en måned: blar du
  // til mai, skal skattemeldingsfristen 31. mai stå der. Se `yearTasks` i
  // autopilot.jsx. `null` betyr «ingen måned» og oppfører seg som før.
  deadlineMonth = null, deadlineId = null, incomeYear = null,
}) => ({
  id,
  icon,
  title,
  desc,
  detail,
  status,
  route,
  actionLabel: actionLabel || (route ? t('ap.task.actionLabel', 'Gå til') : t('ap.task.detailsLabel', 'Se detaljer')),
  readyCount,
  reviewCount,
  blockedReason,
  statusDetail,
  lines,
  inbox,
  kind,
  due,
  scope, bankIntegrityIssue,
  deadlineMonth, deadlineId, incomeYear,
  keepVisibleWhenDone,
  skippable,
  requiresCorrection,
  // Satt bare paa tasks som har et ekte execution-lag i Functions. Uten den
  // viser UI-et bare «Aapne», slik alle backend-tasks gjorde for.
  execution,
  backendBacked: true,
  requiresBackendExecution: ['ready', 'needs_review'].includes(status),
});

const apLine = ({ id, date = '', title, sub = '', amount = null, status = 'matchet', note = '', route = '', data = null, incomeYear = null, requiresCorrection = false }) => ({
  id,
  date,
  title,
  sub,
  amount,
  status,
  note,
  route, data, incomeYear, requiresCorrection,
});

// Maaneder for overtakelsesdatoen ble fort i et annet system. Da har vi verken
// bankutskrift eller MVA-spor for dem, og en oppgave som ber om dem sender
// brukeren rett i en dobbeltforing. Samme sperre som agentene bruker.
const apImportedTask = ({ id, icon, kind, route, month, year, transitionMonth, state, bankOnly = false }) =>
  (window.apAgentImportedPeriod?.(month, transitionMonth, { state, year, bankOnly })
    ? window.apAgentArchiveTask?.({ apTask }, { id, icon, kind, route, month, year })
    : null);

const apBankTask = (bankModel, year, month, transitionMonth, state) => {
  // Banken er fasit bakover: er maaneden avstemt for overtakelsen, skal ikke
  // bankoppgaven be om en utskrift Plumme aldri skulle hatt.
  const imported = apImportedTask({ id: 'bank', icon: 'bank', kind: 'bank', route: 'bank', month, year, transitionMonth, state, bankOnly: true });
  if (imported) return imported;
  // Foer datoen bankkontoen begynner aa avstemmes fra finnes det ingen utskrift
  // aa etterlyse — og ingen annen bokholder aa skylde paa heller.
  if (window.apAgentBeforeBankCoverage?.(month, { state, year })) {
    return window.apAgentBankNotStartedTask?.({ apTask }, {
      id: 'bank', icon: 'bank', kind: 'bank', month, year, from: window.apBankCoverageFrom?.(state),
    }) || null;
  }
  if (!apHasBankReconciliationScope(state)) {
    return apTask({
      id: 'bank',
      icon: 'bank',
      title: t('ap.agent.bank.disabledTitle', 'Bankavstemming er ikke aktiv'),
      desc: t('ap.agent.bank.disabledDesc', 'Ingen aktive bankkontoer er satt til avstemming.'),
      detail: t('ap.agent.bank.disabledDetail', 'Dette passer for foretak som bruker privat konto og har valgt at kontoen ikke skal avstemmes i Bank.'),
      status: apTaskStatus(),
      route: 'bank',
      actionLabel: t('ap.agent.bank.action', 'Åpne bank'),
      kind: 'bank',
      scope: 'month',
    });
  }
  const allStatements = apArray(bankModel?.statements).filter((statement) => statement?.mode !== 'privat' && statement?.mode !== 'private');
  const periodStatements = allStatements.filter((statement) => apBankTaskStatementCovers(statement, year, month));
  const statements = month == null ? allStatements : periodStatements;
  const settledPeriodStatements = month == null ? [] : periodStatements.filter(apBankTaskStatementSettled);
  const activePeriodStatements = periodStatements.filter((statement) => statement.status === 'active');
  if (month != null && settledPeriodStatements.length && !activePeriodStatements.length) {
    const lineCount = settledPeriodStatements.reduce((sum, statement) => sum + apArray(statement.lineItems).length, 0);
    return apTask({
      id: 'bank',
      icon: 'bank',
      title: t('bg.reconciliationDone', 'Bankavstemmingen er fullfort'),
      desc: t('bg.reconciliationDoneHelp', 'Kontoutskriften er kontrollert, bokfort og flyttet til arkivet.'),
      detail: t('ap.agent.bank.reopenDetail', 'Apne avstemmingen igjen hvis noe ma rettes. Da vises bankarbeidsflaten i samme modal.'),
      status: apTaskStatus({ done: true, checked: lineCount }),
      route: 'bank',
      actionLabel: t('bg.reopenReconciliation', 'Apne avstemming igjen'),
      readyCount: 0,
      reviewCount: 0,
      kind: 'bank',
      scope: 'month',
      keepVisibleWhenDone: true,
    });
  }
  const inbox = apArray(bankModel?.inbox);
  const lines = statements
    .flatMap((statement) => apArray(statement.lineItems).map((line) => ({ ...line, statement })))
    .filter((line) => apInPeriod(line.date, year, month));
  const reviewLines = lines.filter((line) => AP_BANK_TASK_REVIEW_STATUSES.includes(apBankTaskLineStatus(line)));
  // Aapne utskrifter der hver linje har faatt et valg. Om de faktisk kan
  // arkiveres (bokforing og saldo) avgjor backend.
  const settledStatements = statements.filter((statement) =>
    statement.status === 'active' &&
    apArray(statement.lineItems).length > 0 &&
    apArray(statement.lineItems).every((line) => !AP_BANK_TASK_REVIEW_STATUSES.includes(apBankTaskLineStatus(line))));
  const handledLines = lines.filter((line) => AP_BANK_TASK_HANDLED_STATUSES.includes(apBankTaskLineStatus(line)));
  const status = statements.length
    ? apTaskStatus({ readyCount: reviewLines.length === 0 ? settledStatements.length : 0, reviewCount: reviewLines.length })
    : apTaskStatus({ blocked: true });
  return apTask({
    id: 'bank',
    icon: 'bank',
    title: statements.length
      ? t('ap.agent.bank.title', 'Bankavstemming: {{count}} linjer trenger kontroll', { count: reviewLines.length })
      : t('ap.agent.bank.titleNoStatement', 'Bankavstemming mangler bankutskrift'),
    desc: statements.length
      ? t('ap.agent.bank.desc', '{{handled}} linjer er behandlet, {{remaining}} står igjen.', { handled: handledLines.length, remaining: reviewLines.length })
      : t('ap.agent.bank.descNoStatement', 'Importer bankutskrift før AI Regnskapsfører kan foreslå bankavstemming.'),
    detail: statements.length
      ? t('ap.agent.bank.detail', 'AI Regnskapsfører viser banklinjer som ikke er ferdig avstemt. Selve bokføringen må fortsatt godkjennes i bankflaten.')
      : t('ap.agent.bank.detailNoStatement', 'Ingen bankutskrift er tilgjengelig for perioden.'),
    status,
    route: 'bank',
    actionLabel: statements.length ? t('ap.agent.bank.action', 'Åpne bank') : t('ap.agent.bank.actionImport', 'Importer bank'),
    readyCount: reviewLines.length === 0 ? settledStatements.length : 0,
    reviewCount: reviewLines.length,
    blockedReason: statements.length ? '' : t('ap.agent.bank.blockedReason', 'Mangler bankdata'),
    lines: reviewLines.slice(0, AP_LINE_CAP).map((line) => apLine({
      id: line.id,
      date: line.date,
      title: line.title,
      sub: line.sub,
      amount: line.amount,
      status: line.status === 'forslag' ? 'kontroll' : line.status === 'avvik' ? 'avvik' : 'mangler',
      note: line.forslag || line.note || '',
      route: 'bank',
    })),
    // Bare forslag med hoy sikkerhet paa aapne driftsutskrifter. Backend gjor
    // den samme vurderingen om igjen, saa dette er et anslag for knappeteksten.
    execution: settledStatements.length ? {
      action: 'archive-settled-statements',
      count: settledStatements.length,
      statementIds: settledStatements.map((statement) => statement.id),
      label: t('ap.agent.bank.archiveLabel', 'Arkiver {{count}} ferdige utskrifter', { count: settledStatements.length }),
      confirmTitle: t('ap.agent.bank.archiveConfirmTitle', 'Arkiver ferdig behandlede bankutskrifter'),
      confirmWarn: t('ap.agent.bank.archiveConfirmWarn', 'Utskriftene flyttes til arkivet og låser perioden. De kan gjenåpnes fra Bank.'),
      checkedNote: t('ap.agent.bank.archiveCheckedNote', 'Bare utskrifter der alle linjer er behandlet, alt er bokført og saldoen går opp. Alt annet må du gjøre fra «Avstem og bokfør».'),
      doneLabel: t('ap.agent.bank.archiveDoneLabel', 'utskrifter arkivert'),
    } : null,
    inbox,
    kind: 'bank',
    scope: 'month',
  });
};

// Levert er levert: `buildVatFilingModel` uttrykker det i `status`, ikke i et
// eget flagg. Den gamle koden leste `term.submitted`, `term.toPay` og
// `term.receivable` — felter modellen aldri har hatt. Da var hver termin «ikke
// levert» og ingen av dem en kandidat, og agenten svarte «MVA er ajour» uansett
// hva som faktisk stod ulevert.
const AP_VAT_SETTLED = ['levert', 'levert-betalt', 'arkiv'];
const apVatSubmitted = (term) => term?.submitted === true || AP_VAT_SETTLED.includes(term?.status);
// Siste dag i terminen. Foer den er passert finnes det ingenting aa levere.
const apVatTermEnd = (term, year) => {
  if (term?.dateTo) {
    const parsed = new Date(`${term.dateTo}T12:00:00`);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  const range = apArray(term?.months);
  const lastMonth = range.length === 2 ? Number(range[1]) : 11;
  return new Date(Number(term?.year || year), lastMonth + 1, 0, 12);
};
const apVatDueDate = (term) => {
  const due = new Date(term?.dueDate);
  return Number.isNaN(due.getTime()) ? null : due;
};
const apVatDueLabel = (term) => term?.frist || (apVatDueDate(term)
  ? apVatDueDate(term).toLocaleDateString(PlummeI18n.intlLocale(), { day: 'numeric', month: 'long', year: 'numeric' })
  : '');
// Siste maaned en frist fra fjoraaret kan lande i. Aarlig MVA forfaller
// 10. mars aaret etter, og ingen terminlengde forfaller senere enn det.
// `autopilotPeriods.test.js` regner etter paa alle terminlengdene, slik at
// tallet ikke kan gli fra fristtabellen i backend.jsx.
const AP_VAT_CARRYOVER_LAST_MONTH = 2;
const apVatTermName = (term) => term?.n
  ? t('ap.agent.vat.termWithNumber', 'termin {{n}}', { n: term.n })
  : (term?.label || t('ap.agent.vat.termFallback', 'MVA-terminen'));
// Februar viser 6. termin i FJOR. Uten aarstallet leste raden som om det gjaldt
// inneværende aar, og «termin 6» i februar 2026 ville vaert en gaate.
const apVatTermLabel = (term, year) => {
  const base = term?.label || (term?.period
    ? t('ap.agent.vat.termWithPeriod', 'MVA {{period}}', { period: term.period })
    : t('ap.agent.vat.termGeneric', 'MVA-termin'));
  const termYear = Number(term?.year);
  return Number.isFinite(termYear) && termYear !== Number(year) ? `${base} ${termYear}` : base;
};

const apVatTask = (vatModel, month, year, transitionMonth, state, today = new Date(), prevVatModel = null) => {
  const imported = apImportedTask({ id: 'mva', icon: 'percent', kind: 'mva', route: 'innsendinger', month, year, transitionMonth, state });
  if (imported) return imported;
  // En termin hoerer til maaneden FRISTEN faller i, og bare den. Terminen som
  // *dekker* maaneden talte ogsaa med foer, og da sto 1. termin til kontroll i
  // januar — en maaned uten en eneste MVA-frist. Agenten ba om noe som ikke
  // kunne gjoeres, og gjorde det tre ganger: januar, februar og april.
  //
  // Det gaar ikke tapt noe med det: den samme terminen staar fortsatt til
  // kontroll i fristmaaneden sin, og Fristagenten varsler naar fristen naermer
  // seg.
  //
  // ------------------------------------------- fristen kan hoere til i fjor
  //
  // En frist i januar, februar eller mars tilhorer som regel FJORAARETS termin:
  // 6. termin (nov-des) forfaller 10. februar, og aarlig MVA 10. mars. De
  // terminene finnes ikke i `getVatFilingModel({ year })` i det hele tatt, og
  // uten fjoraarets modell svarte agenten «Ingen MVA-termin i denne maaneden»
  // om februar — en maaned som har en reell MVA-frist.
  //
  // Bare i maanedsvisningen. Aarsvisningen handler om aarets egne terminer, og
  // skal ikke arve fjoraarets innleveringer.
  const covers = (term) => {
    if (month == null) return true;
    const due = apVatDueDate(term);
    // Uten frist er det ingen fristmaaned aa plassere terminen i. Da faller vi
    // tilbake paa maanedene den dekker — ellers ville terminen blitt usynlig
    // hele aaret, som er verre enn aa staa i feil maaned.
    if (!due) {
      const range = apArray(term.months);
      return range.length !== 2
        || (Number(month) >= Number(range[0]) && Number(month) <= Number(range[1]));
    }
    return due.getFullYear() === Number(year) && due.getMonth() === Number(month);
  };
  const terms = [
    ...apArray(vatModel?.terms),
    ...(month == null ? [] : apArray(prevVatModel?.terms)),
  ].filter(covers);
  // En ulevert termin er en oppgave naar terminen er over — ogsaa med null i
  // netto MVA. Nullmelding skal fortsatt leveres, og den gamle beloepssjekken
  // gjorde en slik termin usynlig.
  const openTerms = terms.filter((term) => !apVatSubmitted(term));
  const deliveredTerms = terms.filter(apVatSubmitted);
  const candidates = openTerms.filter((term) => apVatTermEnd(term, year) <= today);
  // Terminen loeper fortsatt. Da er det ingenting aa kontrollere ennaa, og
  // «MVA er ajour» ville vaert en paastand agenten ikke har dekning for.
  const running = openTerms.filter((term) => apVatTermEnd(term, year) > today);
  const overdue = candidates.filter((term) => {
    const due = apVatDueDate(term);
    return Boolean(due) && due < today;
  });
  if (!vatModel?.vatRegistered) {
    return apTask({
      id: 'mva',
      icon: 'percent',
      title: t('ap.agent.vat.disabledTitle', 'MVA-agenten er ikke aktiv'),
      desc: t('ap.agent.vat.disabledDesc', 'Foretaket står ikke som MVA-registrert i innstillingene.'),
      detail: t('ap.agent.vat.disabledDetail', 'AI Regnskapsfører viser ikke MVA-innsending før foretaket er MVA-registrert.'),
      status: apTaskStatus(),
      route: 'innsendinger',
      kind: 'mva',
      scope: 'month',
    });
  }
  if (!terms.length) {
    return apTask({
      id: 'mva',
      icon: 'percent',
      title: t('ap.agent.vat.noTermTitle', 'Ingen MVA-termin i denne måneden'),
      desc: t('ap.agent.vat.noTermDesc', 'Foretakets MVA-periode har ingen innlevering knyttet til valgt måned.'),
      detail: t('ap.agent.vat.noTermDetail', 'Fristagenten varsler fortsatt når MVA-fristen nærmer seg. Selve MVA-terminen ligger under Innleveringer.'),
      status: apTaskStatus(),
      route: 'innsendinger',
      actionLabel: t('ap.agent.vat.openFilings', 'Åpne innleveringer'),
      kind: 'mva',
      scope: 'month',
    });
  }
  const waitingTerm = running[0];
  return apTask({
    id: 'mva',
    icon: 'percent',
    title: candidates.length
      ? overdue.length
        ? (overdue.length === 1
          ? t('ap.agent.vat.titleOverdueOne', '{{count}} MVA-termin er over fristen', { count: overdue.length })
          : t('ap.agent.vat.titleOverdueMany', '{{count}} MVA-terminer er over fristen', { count: overdue.length }))
        : (candidates.length === 1
          ? t('ap.agent.vat.titleCandidatesOne', '{{count}} MVA-termin må leveres', { count: candidates.length })
          : t('ap.agent.vat.titleCandidatesMany', '{{count}} MVA-terminer må leveres', { count: candidates.length }))
      : waitingTerm
      ? t('ap.agent.vat.titleWaiting', 'MVA-{{term}} er ikke ferdig ennå', { term: apVatTermName(waitingTerm) })
      : t('ap.agent.vat.titleUpToDate', 'MVA er ajour'),
    desc: candidates.length
      ? t('ap.agent.vat.descCandidates', 'MVA-tall er beregnet fra regnskapet og må kontrolleres før innsending.')
      : waitingTerm
      ? t('ap.agent.vat.descWaiting', 'Terminen dekker {{period}} og har frist {{due}}.', { period: waitingTerm.period || 'perioden', due: apVatDueLabel(waitingTerm) || 'senere' })
      : deliveredTerms.length
      ? t('ap.agent.vat.descDelivered', 'MVA-meldingen er levert. Den vises fortsatt her slik at du kan åpne terminen hvis noe må korrigeres.')
      : t('ap.agent.vat.descNoneOpen', 'Ingen åpne MVA-terminer funnet.'),
    detail: t('ap.agent.vat.detail', 'AI Regnskapsfører bruker MVA-tallene i Plumme. Innsending og betaling må fortsatt godkjennes før noe sendes.'),
    // Ingen aapne terminer er et tomt kontrollresultat, ikke en automatisk
    // godkjenning. Brukeren skal finne agenten under «Ingenting aa kontrollere».
    // En termin som fortsatt loeper er heller ingen av delene — den avventer.
    status: apTaskStatus({
      waiting: !candidates.length && Boolean(waitingTerm),
      reviewCount: candidates.length,
      done: !openTerms.length,
      checked: terms.length,
    }),
    // Bare naar raden faktisk avventer. Ellers ville en frist for neste termin
    // ligget som understrek paa en rad som ber om noe helt annet.
    statusDetail: !candidates.length && waitingTerm ? t('ap.agent.vat.dueNote', 'Frist {{due}}', { due: apVatDueLabel(waitingTerm) }) : '',
    route: 'innsendinger',
    actionLabel: t('ap.agent.vat.openFilings', 'Åpne innleveringer'),
    reviewCount: candidates.length,
    lines: [
      ...candidates.map((term) => apLine({
        id: term.key || term.label,
        // `term-6` finnes i hvert eneste aar. Februar viser 6. termin i FJOR, og
        // uten aarstallet ville arbeidsflaten aapnet AARETS termin 6 — feil tall,
        // og «Godkjenn og send inn» ville levert feil melding.
        data: { vatYear: Number(term.year) || Number(year) },
        title: apVatTermLabel(term, year),
        sub: term.period || '',
        amount: apNumber(term.amount ?? term.toPay ?? term.receivable),
        status: (apVatDueDate(term) && apVatDueDate(term) < today) ? 'avvik' : 'kontroll',
        note: apVatDueLabel(term) ? t('ap.agent.vat.dueNote', 'Frist {{due}}', { due: apVatDueLabel(term) }) : '',
        route: 'innsendinger',
      })),
      ...(!candidates.length ? deliveredTerms.map((term) => apLine({
        id: term.key || term.label,
        data: { vatYear: Number(term.year) || Number(year) },
        title: apVatTermLabel(term, year),
        sub: term.period || '',
        amount: apNumber(term.amount ?? term.toPay ?? term.receivable),
        status: 'godkjent',
        note: term?.status === 'levert-betalt' ? t('ap.agent.vat.paidNote', 'Levert og betalt') : t('ap.agent.vat.filedNote', 'Levert'),
        route: 'innsendinger',
      })) : []),
    ],
    keepVisibleWhenDone: deliveredTerms.length > 0,
    kind: 'mva',
    scope: 'month',
  });
};

const AP_ASSET_ACCOUNT_CODES = ['1200', '1230', '1280'];
const apAssetsTask = (state, year, month) => {
  // A registered asset is not an open issue. Month Control only owns purchases
  // in the selected period that are posted to an asset account but still lack
  // the corresponding asset-register entry.
  const periodExpenses = apArray(state?.expenses).filter((expense) => apInPeriod(expense.date, year, month));
  const unregistered = periodExpenses.filter((expense) =>
    expense.status !== 'annulled' && expense.status !== 'draft' &&
    !expense.fixedAsset &&
    apNumber(expense.amountNet) >= 15000 &&
    AP_ASSET_ACCOUNT_CODES.includes(String(expense.costAccountCode || '')));
  return apTask({
    id: 'eiendeler',
    icon: 'briefcase',
    title: unregistered.length
      ? t('ap.agent.asset.title', 'Vurder {{count}} kjøp som eiendel', { count: unregistered.length })
      : t('ap.agent.asset.titleEmpty', 'Ingen eiendelsforslag'),
    desc: unregistered.length
      ? t('ap.agent.asset.desc', 'Kjøp ført på eiendelskonto mangler i eiendelsregisteret.')
      : t('ap.agent.asset.descEmpty', 'Ingen kjøp i perioden må vurderes.'),
    detail: t('ap.agent.asset.detail', 'Registrerte eiendeler følges opp i eiendelsregisteret og årsavslutningen.'),
    status: apTaskStatus({ reviewCount: unregistered.length, done: unregistered.length === 0, checked: periodExpenses.length }),
    route: 'eiendeler',
    actionLabel: t('ap.agent.asset.action', 'Åpne eiendeler'),
    reviewCount: unregistered.length,
    execution: unregistered.length ? {
      action: 'register-fixed-assets',
      count: unregistered.length,
      expenseIds: unregistered.map((expense) => expense.id),
      year,
      label: t('ap.agent.asset.registerLabel', 'Registrer {{count}} driftsmidler', { count: unregistered.length }),
      confirmTitle: t('ap.agent.asset.registerConfirmTitle', 'Registrer driftsmidler'),
      confirmWarn: t('ap.agent.asset.registerConfirmWarn', 'Kjøpene får et avskrivningsgrunnlag. Beløp, dato, konto og MVA på selve kjøpet endres ikke.'),
      checkedNote: t('ap.agent.asset.registerCheckedNote', 'Bare kjøp der kontoen bestemmer saldogruppen registreres. Er gruppen uklar, må du velge selv — feil gruppe gir feil avskrivningssats i alle framtidige år.'),
      doneLabel: t('ap.agent.asset.registerDoneLabel', 'driftsmidler registrert'),
    } : null,
    lines: unregistered.slice(0, AP_LINE_CAP).map((expense) => apLine({
      id: expense.id,
      date: expense.date || '',
      title: expense.description || expense.supplierName || t('ap.agent.asset.lineFallback', 'Kjøp'),
      sub: String(expense.costAccountCode || ''),
      amount: apNumber(expense.amountNet),
      status: 'kontroll',
      route: 'eiendeler',
    })),
    scope: 'month',
  });
};

const apPayrollPeriod = () => {
  // Forrige maaned: lonn for en maaned som ikke er over kan ikke kjores ferdig.
  const now = new Date();
  const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${previous.getFullYear()}-${String(previous.getMonth() + 1).padStart(2, '0')}`;
};

const apPayrollTask = (state, year, month, today) => {
  // Lonnskjoringens `period` er 'YYYY-MM'. Med en valgt maaned handler oppgaven
  // om den maaneden, ikke om alle kjoringer i aaret.
  const inMonth = (run) => {
    if (month == null) return true;
    const period = String(run.period || run.month || '');
    const expected = `${year}-${String(Number(month) + 1).padStart(2, '0')}`;
    return period.slice(0, 7) === expected;
  };
  const runs = apArray(state?.payrollRuns).filter(inMonth);
  const openRuns = runs.filter((run) => run.status === 'draft' || run.status === 'submitted');
  const approvedRuns = runs.filter((run) => run.status === 'approved' || run.status === 'paid');
  // Uten valgt maaned er «perioden» forrige maaned; med en valgt maaned er det
  // den maaneden brukeren staar i.
  const period = month == null
    ? apPayrollPeriod()
    : `${year}-${String(Number(month) + 1).padStart(2, '0')}`;
  const employeesWithPay = apArray(state?.employees).filter((employee) =>
    employee.active !== false && (
      apArray(employee.fixedPayrollLines).some((line) => apNumber(line.amount) > 0) ||
      apNumber(employee.monthlySalary) > 0 ||
      apNumber(employee.agreedAnnualSalary) > 0));
  const periodHasRun = runs.some((run) => String(run.period || '').slice(0, 7) === period && run.status !== 'reversed');
  const waiting = runs.length === 0 && Boolean(window.apPeriodIsFuture?.(year, month, today));
  const canGenerate = !waiting && employeesWithPay.length > 0 && !periodHasRun;
  return apTask({
    id: 'lonn',
    icon: 'wallet',
    title: waiting
      ? t('ap.agent.payroll.titleWaiting', 'Lønn for {{period}} avventer', { period })
      : openRuns.length
      ? (openRuns.length === 1
        ? t('ap.agent.payroll.titleOpenOne', '{{count}} lønnskjøring venter', { count: openRuns.length })
        : t('ap.agent.payroll.titleOpenMany', '{{count}} lønnskjøringer venter', { count: openRuns.length }))
      : canGenerate
      ? t('ap.agent.payroll.titleReady', 'Lønnskjøring for {{period}} kan opprettes', { period })
      : t('ap.agent.payroll.titleNoRuns', 'Lønn har ingen åpne kjøringer'),
    desc: waiting
      ? t('ap.agent.payroll.descWaiting', 'Perioden har ikke begynt ennå. Lønnsagenten følger opp når måneden er aktuell.')
      : openRuns.length
      ? t('ap.agent.payroll.descOpen', 'Lønn må kontrolleres og godkjennes i lønnsflaten.')
      : canGenerate
      ? t('ap.agent.payroll.descReady', 'Faste lønnslinjer er klare til å opprettes som et utkast for kontroll.')
      : (approvedRuns.length === 1
        ? t('ap.agent.payroll.descApprovedOne', '{{count}} lønnskjøring er godkjent eller betalt.', { count: approvedRuns.length })
        : t('ap.agent.payroll.descApprovedMany', '{{count}} lønnskjøringer er godkjent eller betalt.', { count: approvedRuns.length })),
    detail: t('ap.agent.payroll.detail', 'AI Regnskapsfører viser lønnsstatus fra Plumme. Godkjenning og betaling gjøres i lønnsflaten. Innsending av A-melding til Altinn er ikke koblet på ennå.'),
    status: apTaskStatus({ waiting, reviewCount: openRuns.length, readyCount: canGenerate ? 1 : 0, done: runs.length > 0 && openRuns.length === 0, checked: runs.length }),
    statusDetail: waiting ? t('ap.agent.payroll.waitingReason', 'Venter på at perioden starter') : '',
    route: 'lonn',
    actionLabel: t('ap.agent.payroll.action', 'Åpne lønn'),
    reviewCount: openRuns.length,
    lines: openRuns.slice(0, AP_LINE_CAP).map((run) => apLine({
      id: run.id,
      date: run.period || run.month || '',
      title: run.title || run.name || t('ap.agent.payroll.lineFallback', 'Lønnskjøring'),
      sub: run.status || '',
      amount: apNumber(run.totalGross || run.totalNet || run.amount),
      status: 'kontroll',
      route: 'lonn',
    })),
    // Autopiloten lager utkastet; godkjenning krever at brukeren aapner
    // lonnsflaten og ser hva hver ansatt faktisk faar.
    execution: canGenerate ? {
      action: 'generate-payroll-run',
      count: employeesWithPay.length,
      period,
      label: t('ap.agent.payroll.generateLabel', 'Lag lønnskjøring for {{period}}', { period }),
      confirmTitle: t('ap.agent.payroll.generateConfirmTitle', 'Lag lønnskjøring som utkast'),
      confirmWarn: t('ap.agent.payroll.generateConfirmWarn', 'Kjøringen lages som utkast fra faste lønnslinjer. Ingenting utbetales eller bokføres — du må åpne lønn, kontrollere hver ansatt og godkjenne selv.'),
      checkedNote: t('ap.agent.payroll.generateCheckedNote', 'Reiserefusjoner tas ikke med. De må legges til manuelt, slik at ingen refusjon utbetales uten at reisen er vurdert.'),
      doneLabel: t('ap.agent.payroll.generateDoneLabel', 'lønnskjøring laget'),
    } : null,
    scope: 'month',
  });
};

// Hvilke måneder som faktisk er ferdig avstemt i året. Kilden er avstemte/bokførte
// driftsutskrifter — det er den eneste harde «måneden er lukket»-datoen vi har.
// Alt annet ville vært en gjetning presentert som fasit.
const apReconciledMonths = (bankModel, activeYear) => {
  const months = new Array(12).fill(false); if (bankModel?.bankControl?.year === activeYear && window.PlummeBankPeriodControl) return window.PlummeBankPeriodControl.reconciledMonths(bankModel.bankControl);
  apArray(bankModel?.statements).forEach((statement) => {
    if (statement.mode !== 'drift') return;
    const status = String(statement.status || '');
    const settled = ['archived', 'reconciled', 'booked'].includes(status) || Boolean(statement._backend?.bookedAt);
    if (!settled) return;
    const from = new Date(statement.periodFrom);
    const to = new Date(statement.periodTo || statement.periodFrom);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return;
    for (let cursor = new Date(from.getFullYear(), from.getMonth(), 1); cursor <= to; cursor.setMonth(cursor.getMonth() + 1)) {
      if (cursor.getFullYear() === activeYear) months[cursor.getMonth()] = true;
    }
  });
  return months;
};

// `month` er 0-11, eller null for «hele aaret». Uten den var maanedsvelgeren
// dekorativ: alle agentene filtrerte bare paa aar, og januar kunne vise et salg
// fra mars.
function buildAiAutopilotModel({ year, month = null, today = new Date() } = {}) {
  const backend = window.PlummeBackend || null;
  const state = backend?.appState || null;
  const activeYear = Number(year || state?.activeYear) || new Date().getFullYear();
  const homeModel = backend?.getHomeDashboardModel?.() || null;
  const bankModel = backend?.getBankPageModel?.() || null;
  const vatModel = backend?.getVatFilingModel?.({ year: activeYear }) || null;
  // Fjoraarets terminer kan ha frist i dette aaret: 6. termin forfaller
  // 10. februar og aarlig MVA 10. mars. Senere enn mars kommer ingen frist fra
  // i fjor, saa modellen bygges bare naar den kan bety noe — den er tung nok
  // til at et maanedsklikk ikke skal betale for den uten grunn.
  const prevVatModel = month != null && Number(month) <= AP_VAT_CARRYOVER_LAST_MONTH
    ? backend?.getVatFilingModel?.({ year: activeYear - 1 }) || null
    : null;
  const invoiceModel = backend?.getInvoiceListModel?.({ year: activeYear }) || null;
  const inboxModel = backend?.getReceiptInboxModel?.() || null;
  const taxModel = backend?.getTaxReturnModel?.({ year: activeYear }) || null;
  const annualModel = backend?.getAnnualAccountsModel?.({ year: activeYear }) || null;
  const reconciledMonths = apReconciledMonths(bankModel, activeYear);

  const transitionMonth = window.apTransitionMonth?.(state, activeYear) ?? null;

  const tasks = [
    ...(window.apBuildFollowupTasks?.({ homeModel, state, year: activeYear, month, today }) || []),
    apBankTask(bankModel, activeYear, month, transitionMonth, state),
    apVatTask(vatModel, month, activeYear, transitionMonth, state, today, prevVatModel),
    apAssetsTask(state, activeYear, month),
    apPayrollTask(state, activeYear, month, today),
    // Agentene som ikke fantes i den opprinnelige lista ligger i
    // `autopilot-agents.jsx`. De bygges bare når backend har levert state —
    // uten data ville de bare vært en liste med nuller.
    ...(state ? apArray(window.buildAutopilotAgentTasks?.({
      state,
      year: activeYear,
      today,
      homeModel,
      bankModel,
      invoiceModel,
      inboxModel,
      taxModel,
      annualModel,
      reconciledMonths,
      month,
      // Maaneder for overtakelsen er fort i et annet system. Agentene skal ikke
      // uttale seg om dem, verken positivt eller negativt.
      transitionMonth,
      transitionDate: state?.openingBalanceByYear?.[String(activeYear)]?.transitionDate || '',
    })) : []),
  ];

  // Godkjenninger er skrevet av Functions med kontrollspor. Klienten leser dem,
  // men skriver dem aldri selv.
  //
  // Maanedssignaturen er borte. Den var et ekstra steg over godkjenningene
  // brukeren allerede hadde gitt agent for agent, uten aa vaere et
  // regnskapsmessig krav — og en maaned som var «godkjent» mens noe sto aapent
  // var nettopp den doble sannheten signaturen skulle hindre. Er alle agentene
  // i maaneden gjennomgaatt, er maaneden gjennomgaatt.
  const reviews = state?.autopilotReviews || {};
  const periodKey = month == null
    ? ''
    : `${activeYear}-${String(Number(month) + 1).padStart(2, '0')}`;
  const reviewTaskList = () => tasks.map((task) => {
    const review = window.apReviewForTask(reviews, task, activeYear, periodKey);
    const approvedAt = review?.at || '';
    // `wasIdle` huskes fordi godkjenningen overskriver statusen. Uten den ville
    // en agent som ikke fant noe hoppet ut av «ingenting aa kontrollere»-gruppa
    // i det du signerte, og lista ville vokst av at du ryddet i den.
    // En oppgave som ennaa ikke kan vurderes skal aldri bli ferdig bare fordi
    // det finnes en eldre signatur eller en uventet periodelukking i state.
    // Grunnlaget foelger med paa hver oppgave: klienten sender det med
    // godkjenningen, slik at signaturen vet hva den dekket.
    const currentBasis = apTaskBasis(task);
    if (task.bankIntegrityIssue || task.requiresCorrection || task.status === 'waiting' || (['frister', 'faktura'].includes(task.kind) && task.reviewCount > 0)) return { ...task, basis: currentBasis };
    // ---------------------------------------------- klarert uten et tastetrykk
    //
    // En agent uten noe aa vurdere hadde en Godkjenn-knapp: et klikk som bare
    // bekreftet at lista var tom. Med sju slike agenter i en rolig maaned var
    // det sju klikk foer maaneden kunne godkjennes, og hvert av dem skrev en
    // signatur paa ingenting.
    //
    // `idle` klareres derfor av seg selv. Det er ikke en signatur — raden viser
    // hva agenten saa etter, og dukker opp igjen med en gang den finner noe.
    if (!approvedAt) return { ...task, basis: currentBasis, autoCleared: task.status === 'idle' };
    // Hva signaturen faktisk dekket. Godkjenninger fra for dette ble innfort
    // har ingen — de regnes som dekkende, ellers ville alt sett foreldet ut paa
    // en gang uten at noe hadde endret seg.
    const signedBasis = apAgentText(review?.basisSignature);
    const stale = Boolean(signedBasis && currentBasis && signedBasis !== currentBasis);
    // Har grunnlaget endret seg etter signaturen, dekker den ikke lenger. Da
    // beholder oppgaven statusen agenten gir den naa — det er den som er sann —
    // mens signaturen blir staaende som spor paa raden.
    if (stale) {
      // Grunnlaget er endret, men agenten finner ingenting naa. Da er det ikke
      // noe igjen for signaturen aa daekke, og raden skal ikke be om et klikk
      // for aa bekrefte en tom liste paa nytt. Uten dette sto MVA-agenten paa
      // «Ingenting aa kontrollere» med en Godkjenn-knapp ved siden av, i en
      // maaned som allerede var godkjent.
      //
      // «Godkjent {dato} · grunnlaget er endret siden» faller ogsaa bort:
      // over en rad uten funn er det stoy, ikke en opplysning. Selve posten
      // ligger urort i state — det er dekningen som er borte, ikke historikken.
      // `approvedAt` fjernes fordi «Angre godkjenning» ellers ville tilbudt aa
      // ta tilbake en signatur backend ikke lenger finner noe aa angre paa.
      if (task.status === 'idle') {
        return { ...task, basis: currentBasis, autoCleared: true, wasIdle: true };
      }
      return { ...task, basis: currentBasis, approvedAt, approvalStale: true, wasIdle: false };
    }
    return { ...task, basis: currentBasis, status: 'done', approvedAt, wasIdle: task.status === 'idle' };
  });
  const reviewedTasks = reviewTaskList();

  // Uten backend eller mens state lastes skal siden være tom. Produksjonskoden
  // har ingen design-/demodata som reserve.
  const source = state ? 'backend' : backend ? 'loading' : 'empty';

  return {
    year: activeYear,
    source,
    // Ingen bankkobling finnes i produktet ennå. Dette betyr «bankutskrift er
    // importert», ikke «banken er tilkoblet».
    bankImported: apArray(bankModel?.statements).length > 0,
    bankLabel: apArray(bankModel?.accounts)[0]?.label || '',
    reconciledMonths,
    // Status for alle tolv maanedene. Maa regnes over hele aaret: fargen paa en
    // maanedsknapp kan ikke utledes av oppgavelista for den valgte maaneden.
    monthStatuses: window.apMonthStatuses?.({
      state,
      invoiceModel,
      inboxModel,
      reconciledMonths,
      bankImported: apArray(bankModel?.statements).length > 0, bankControl: bankModel?.bankControl,
      year: activeYear,
      today,
    }) || new Array(12).fill(''),
    readyCount: reviewedTasks.filter((task) => task.status === 'ready').length,
    reviewCount: reviewedTasks.filter((task) => task.status === 'needs_review').length,
    blockedCount: reviewedTasks.filter((task) => task.status === 'blocked').length,
    doneCount: reviewedTasks.filter((task) => task.status === 'done').length,
    tasks: reviewedTasks,
    periodKey,
  };
}

// Task-byggerne i `autopilot-agents.jsx` bruker samme hjelpere, slik at alle
// tasks får identisk form uansett hvilken fil de er bygget i.
Object.assign(window, {
  buildAiAutopilotModel,
  apHasBankReconciliationScope,
  apBankReconcileAccounts,
  apBankAccountIdentity,
  apTaskHelpers: { apTask, apLine, apArray, apNumber, apTaskStatus, AP_LINE_CAP },
});
